/**
 * SheerID Special Personnel Verification Modal
 * Ported from H5 project (SpecialPersonnelDialog.vue), reimplemented as pure vanilla JS.
 *
 * Public API:
 *   - window.SheerIdModal.open(triggerEl?)
 *   - window.SheerIdModal.close()
 *
 * Trigger via:
 *   - <a href="#open-sheerid">  (recommended for merchant button settings)
 *   - <a href="#open-sheerid-modal">
 *   - <button data-action="open-sheerid">
 */
(function () {
  'use strict';

  var MODAL_ID = 'SheerIdModal';
  var DEFAULT_ALLOWED_COUNTRIES = ['US', 'CA', 'GB'];
  var SHEERID_CSS = 'https://cdn.jsdelivr.net/npm/@sheerid/jslib@1.165.0/sheerid.css';
  var SHEERID_JS = 'https://cdn.jsdelivr.net/npm/@sheerid/jslib@1.165.0/sheerid.js';

  var FOCUSABLE_SEL =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  // 直接复用 H5 项目的 originListObj，不改 key 和 supportList
  var CATEGORIES = [
    {
      name: 'Teachers',
      key: '69e1e57c0a910b6242d6c2c2',
      supportList: ['US','CA','GB','DE','IT','FR','AT','ES','CH','NL','IE','SE','NO','FI','DK','MX','BE','PL','CZ','PT','LU','GR','LT','EE','HU','BG','RO','HR','LV','MT','SK','SI','JP','KR','SG','TH','PH','MY','VN','TR','KZ','AE','KW','SA','QA','BH','OM','RU','IL','ID','HK','CN','TW','JO','MO','AU','NZ','ZA']
    },
    {
      name: 'Students',
      key: '69e1dcf833dccb611f8a904b',
      supportList: ['US','CA','GB','DE','IT','FR','AT','ES','CH','NL','IE','SE','NO','FI','DK','MX','BE','PL','CZ','PT','LU','GR','LT','EE','HU','BG','RO','HR','LV','MT','SK','SI','JP','KR','SG','TH','PH','MY','VN','TR','KZ','AE','KW','SA','QA','BH','OM','RU','IL','ID','HK','CN','TW','JO','MO','AU','NZ','ZA']
    },
    {
      name: 'Military',
      key: '69e1ebb133dccb611f8b29a5',
      supportList: ['US','CA','GB']
    },
    {
      name: 'Medical Professionals',
      key: '69e1ecd833dccb611f8b4174',
      supportList: ['US','CA','GB','DE','IT','FR','AT','ES','CH','NL','IE','SE','NO','FI','DK']
    },
    {
      name: 'First Responders',
      key: '69e1ef2e33dccb611f8b72ed',
      supportList: ['US','CA','GB','DE','IT','FR','AT','ES','CH','NL','IE']
    }
  ];

  var previouslyFocused = null;
  var savedScrollY = 0;
  var i18nMap = null;
  var sheerIdReady = null;

  function getRoot() { return document.getElementById(MODAL_ID); }

  function getCurrentCountry() {
    var root = getRoot();
    return (root && root.getAttribute('data-country') || 'US').toUpperCase();
  }

  function getAllowedCountries() {
    var root = getRoot();
    if (!root) return DEFAULT_ALLOWED_COUNTRIES.slice();
    var raw = root.getAttribute('data-allowed-countries') || '';
    var list = raw.split(',').map(function (code) {
      return String(code || '').trim().toUpperCase();
    }).filter(Boolean);
    return list.length ? list : DEFAULT_ALLOWED_COUNTRIES.slice();
  }

  function isCountryAllowed(country) {
    return getAllowedCountries().indexOf(String(country || '').toUpperCase()) !== -1;
  }

  function setActivePanel(panelName) {
    var root = getRoot();
    if (!root) return;
    root.querySelectorAll('[data-sheerid-panel]').forEach(function (panel) {
      var isActive = panel.getAttribute('data-sheerid-panel') === panelName;
      panel.hidden = !isActive;
    });
    root.setAttribute('aria-labelledby', panelName === 'unsupported' ? '' : 'SheerIdModal-title');
  }

  function getI18nMap() {
    if (i18nMap) return i18nMap;
    var el = document.querySelector('script[data-sheerid-modal-i18n]');
    if (!el) { i18nMap = {}; return i18nMap; }
    try { i18nMap = JSON.parse(el.textContent.trim()) || {}; }
    catch (e) { i18nMap = {}; }
    return i18nMap;
  }

  /**
   * 渲染分类列表 — 根据国家代码过滤 CATEGORIES、用 i18n map 翻译显示名。
   * - 调用时机：每次 open() 之前，因为商家可能用 Shopify Markets 切换国家
   */
  function renderCategories() {
    var root = getRoot();
    if (!root) return;
    var country = getCurrentCountry();
    var ul = root.querySelector('[data-category-list]');
    if (!ul) return;

    ul.innerHTML = '';
    var i18n = getI18nMap();
    var visible = CATEGORIES.filter(function (c) {
      return c.supportList.indexOf(country) !== -1;
    });

    visible.forEach(function (cat) {
      var li = document.createElement('li');
      li.className = 'sheerid-modal__category';
      li.setAttribute('role', 'button');
      li.setAttribute('tabindex', '0');
      li.textContent = i18n[cat.name] || cat.name;
      li.addEventListener('click', function () { openSheerIdIframe(cat.key); });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openSheerIdIframe(cat.key);
        }
      });
      ul.appendChild(li);
    });
  }

  /**
   * 打开弹窗，参数：
   * - triggerEl：触发元素，关闭时焦点会归还给它
   */
  function open(triggerEl) {
    var root = getRoot();
    if (!root) return;

    previouslyFocused = (triggerEl && triggerEl.focus) ? triggerEl : document.activeElement;

    var country = getCurrentCountry();
    if (!isCountryAllowed(country)) {
      setActivePanel('unsupported');
    } else {
      renderCategories();
      setActivePanel('categories');
    }

    // iOS-safe 滚动锁：固定 body 位置，记住滚动偏移
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');

    document.addEventListener('keydown', onKeydown, true);
    root.addEventListener('click', onMaskClick);

    var content = root.querySelector('.sheerid-modal__content');
    if (content) {
      // 推迟到下一帧，避免 hidden 切换时焦点设置失败
      requestAnimationFrame(function () { content.focus(); });
    }
  }

  function close() {
    var root = getRoot();
    if (!root || root.hidden) return;

    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    setActivePanel('categories');

    document.removeEventListener('keydown', onKeydown, true);
    root.removeEventListener('click', onMaskClick);

    // 还原滚动
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);

    if (previouslyFocused && previouslyFocused.focus) {
      try { previouslyFocused.focus(); } catch (e) { /* noop */ }
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') trapFocus(e);
  }

  function onMaskClick(e) {
    if (e.target.closest('[data-sheerid-close]')) {
      e.preventDefault();
      close();
    }
  }

  function trapFocus(e) {
    var root = getRoot();
    if (!root) return;
    var items = root.querySelectorAll(FOCUSABLE_SEL);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  /**
   * 懒加载 SheerID 的 CSS/JS，只在第一次点击分类时触发
   * - 返回 Promise，resolve 时 window.sheerId 已可用
   */
  function ensureSheerId() {
    if (sheerIdReady) return sheerIdReady;
    if (window.sheerId) {
      sheerIdReady = Promise.resolve(window.sheerId);
      return sheerIdReady;
    }

    sheerIdReady = new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = SHEERID_CSS;
      document.head.appendChild(link);

      var script = document.createElement('script');
      script.src = SHEERID_JS;
      script.async = true;
      script.onload = function () { resolve(window.sheerId); };
      script.onerror = function () {
        sheerIdReady = null; // 允许重试
        reject(new Error('Failed to load SheerID script'));
      };
      document.head.appendChild(script);
    });
    return sheerIdReady;
  }

  /**
   * locale 适配规则（与 H5 项目保持一致）：
   * - 加拿大 + 法语 -> fr-CA
   * - 西班牙 + 西语 -> es-ES
   * - 其它情况 -> Shopify.locale 或 en
   */
  function resolveLocale() {
    var root = getRoot();
    var country = (root && root.getAttribute('data-country') || '').toUpperCase();
    var lang = (window.Shopify && window.Shopify.locale) ||
               (root && root.getAttribute('data-locale')) || 'en';
    lang = String(lang).toLowerCase().split('-')[0];

    if (country === 'CA' && lang === 'fr') return 'fr-CA';
    if (country === 'ES' && lang === 'es') return 'es-ES';
    return lang || 'en';
  }

  /**
   * 打开第二层弹窗（SheerID iframe）
   * - key：SheerID 分类 ID，例如 Teachers = '617a5013e4f52f7ebd688ccd'
   */
  function openSheerIdIframe(key) {
    ensureSheerId().then(function (sheerId) {
      if (!sheerId || typeof sheerId.loadInlineIframe !== 'function') {
        console.warn('[SheerIdModal] sheerId.loadInlineIframe unavailable');
        return;
      }

      var root = getRoot();
      var country = (root && root.getAttribute('data-country') || '').toUpperCase();
      var locale = resolveLocale();
      var verificationUrl = 'https://services.sheerid.com/verify/' + key + '/?locale=' + locale;

      var overlay = buildIframeOverlay();
      document.body.appendChild(overlay);

      var host = overlay.querySelector('.sheerid-iframe-host');
      var frame = sheerId.loadInlineIframe(host, verificationUrl);
      if (frame && typeof frame.setViewModel === 'function') {
        frame.setViewModel({ countryChoice: { label: '', value: country } });
      }
    }).catch(function (err) {
      console.warn('[SheerIdModal]', err);
    });
  }

  function buildIframeOverlay() {
    var overlay = document.createElement('div');
    overlay.className = 'sheerid-iframe-overlay';

    var container = document.createElement('div');
    container.className = 'sheerid-iframe-overlay__container';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'sheerid-iframe-overlay__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 18 17" width="14" height="14" fill="none">' +
      '<path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"/>' +
      '</svg>';
    closeBtn.addEventListener('click', function () { overlay.remove(); });
    container.appendChild(closeBtn);

    var host = document.createElement('div');
    host.className = 'sheerid-iframe-host';
    container.appendChild(host);

    overlay.appendChild(container);
    return overlay;
  }

  // 全局事件委托：让 PageFly/Ecomposer 异步注入的按钮也能触发
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(
      'a[href="#open-sheerid" i], a[href="#open-sheerid-modal" i], [data-action="open-sheerid"]'
    );
    if (!trigger) return;
    e.preventDefault();
    open(trigger);
  });

  window.SheerIdModal = {
    open: open,
    close: close,
    isCountryAllowed: isCountryAllowed,
    getCurrentCountry: getCurrentCountry
  };
})();
